/**
 * AiCloud CLI Download - Python CLI indirme sayfasi
 * Windows/Mac/Linux binary + pip install
 */

import axios from 'axios'
import { Check, Copy, Download, Monitor, Server, Terminal } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../config/api'

interface CLIRelease {
  version: string
  released_at: string
  windows_url: string
  macos_url: string
  linux_url: string
  changelog: string[]
}

export default function CLIDownloadPage() {
  const [release, setRelease] = useState<CLIRelease | null>(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState<string | null>(null)

  useEffect(() => {
    const fetch = async () => {
      try {
        const resp = await axios.get(`${API_URL}/api/aicloud/cli/latest-release`)
        setRelease(resp.data as CLIRelease)
      } catch {
        // Fallback - backend yoksa statik goster
        setRelease({
          version: '1.0.0',
          released_at: '2026-03-15',
          windows_url: '/downloads/aicloud-cli-1.0.0-windows.exe',
          macos_url: '/downloads/aicloud-cli-1.0.0-macos',
          linux_url: '/downloads/aicloud-cli-1.0.0-linux',
          changelog: [
            'Initial release',
            'Cloudflare, Azure, GCP, AWS support',
            'Local scrapers (Facebook, Twitter, Gmail, WhatsApp)',
            'OpenClaw skills integration',
            'Offline mode support'
          ]
        })
      } finally {
        setLoading(false)
      }
    }
    void fetch()
  }, [])

  const copyToClipboard = (text: string, key: string) => {
    void navigator.clipboard.writeText(text)
    setCopied(key)
    setTimeout(() => setCopied(null), 2000)
  }

  const CodeBlock = ({ code, copyKey }: { code: string; copyKey: string }) => (
    <div style={{ position: 'relative', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '12px 40px 12px 14px', fontFamily: 'monospace', fontSize: '13px', color: 'var(--text-primary)' }}>
      {code}
      <button
        onClick={() => copyToClipboard(code, copyKey)}
        style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: copied === copyKey ? '#10b981' : 'var(--text-secondary)', padding: '4px' }}>
        {copied === copyKey ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </div>
  )

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>AiCloud CLI</h2>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          Python CLI for advanced cloud management, local scrapers, and direct infrastructure control.
        </p>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Versiyon bilgisi */}
          {release && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '8px' }}>
              <Terminal size={16} color="#3b82f6" />
              <span style={{ fontSize: '13px' }}>Latest version: <strong>v{release.version}</strong></span>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Released {release.released_at}</span>
            </div>
          )}

          {/* Kurulum yontemleri */}
          <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Install via pip (Recommended)</h3>
            <CodeBlock code="pip install aicloud-cli" copyKey="pip" />
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }}>
              Requires Python 3.10+. Installs all dependencies automatically.
            </p>
          </div>

          {/* Binary indirme */}
          <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Download Binary (No Python Required)</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
              {[
                { icon: Monitor, label: 'Windows', desc: '.exe binary', url: release?.windows_url, color: '#0078d4' },
                { icon: Server, label: 'macOS', desc: 'Universal binary', url: release?.macos_url, color: '#555' },
                { icon: Terminal, label: 'Linux', desc: 'x86_64 binary', url: release?.linux_url, color: '#f59e0b' },
              ].map(p => (
                <a key={p.label} href={p.url ?? '#'}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', textDecoration: 'none', color: 'var(--text-primary)', transition: 'border-color 0.15s' }}>
                  <p.icon size={20} color={p.color} />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{p.label}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{p.desc}</div>
                  </div>
                  <Download size={14} color="var(--text-secondary)" style={{ marginLeft: 'auto' }} />
                </a>
              ))}
            </div>
          </div>

          {/* Hizli baslangic */}
          <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Quick Start</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { step: '1', label: 'Initialize', code: 'aicloud init' },
                { step: '2', label: 'Deploy to your cloud', code: 'aicloud deploy --provider=cloudflare' },
                { step: '3', label: 'Check status', code: 'aicloud status' },
                { step: '4', label: 'Run a scraper', code: 'aicloud scrape facebook --output=cloudflare' },
                { step: '5', label: 'List skills', code: 'aicloud skills list' },
              ].map(item => (
                <div key={item.step} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: '#3b82f6', flexShrink: 0 }}>
                    {item.step}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{item.label}</div>
                    <CodeBlock code={item.code} copyKey={`step-${item.step}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Komutlar */}
          <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>Available Commands</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {[
                { cmd: 'aicloud init', desc: 'Interactive setup wizard' },
                { cmd: 'aicloud deploy --provider=<cf|azure|gcp|aws>', desc: 'Deploy to your cloud' },
                { cmd: 'aicloud status', desc: 'Show resource status' },
                { cmd: 'aicloud health', desc: 'Health check all resources' },
                { cmd: 'aicloud usage --period=month', desc: 'Show usage statistics' },
                { cmd: 'aicloud cost --provider=all', desc: 'Cost breakdown' },
                { cmd: 'aicloud scrape facebook', desc: 'Run Facebook scraper' },
                { cmd: 'aicloud scrape twitter --query="AI news"', desc: 'Run Twitter scraper' },
                { cmd: 'aicloud scrape gmail --label=Important', desc: 'Run Gmail scraper' },
                { cmd: 'aicloud skills list', desc: 'List available skills' },
                { cmd: 'aicloud skill run web_search --query="..."', desc: 'Execute a skill' },
                { cmd: 'aicloud export --format=json', desc: 'Export all data' },
                { cmd: 'aicloud disconnect --provider=cloudflare', desc: 'Disconnect provider' },
              ].map(item => (
                <div key={item.cmd} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 10px', background: 'var(--body-bg)', borderRadius: '6px' }}>
                  <code style={{ fontSize: '12px', color: '#3b82f6', fontFamily: 'monospace', minWidth: '280px' }}>{item.cmd}</code>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{item.desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Changelog */}
          {release && release.changelog.length > 0 && (
            <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>v{release.version} Changelog</h3>
              <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {release.changelog.map((item, i) => (
                  <li key={i} style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
